import React, { useMemo, useState } from 'react';
import { AutoComplete, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  capabilitiesData,
  ICapability,
} from '../../../../../constants/CapabilitiesConstants';
import { DashboardHeaderConstants } from '../../../../../pages/Dashboard/DashboardHeader/DashboardHeaderConstants';
import { DashboardActions } from '../../../../../libs/store/actions/DashboardActions';
import { SidebarActions } from '../../../../../libs/store/actions/SidebarActions';
import { routes } from '../../../../../navigation/Routes';
import { SideBarKey } from '../../Sidebar/SidebarConstants';
import './CapabilitySearch.less';

type EntryKind = 'domain' | 'capability';

interface ISearchEntry {
  key: string;
  label: string;
  description?: string;
  kind: EntryKind;
  tags: string[];
  // For domain entries
  dashboardTitle?: string;
  sidebarKey?: SideBarKey | string;
  // For capability entries
  route?: string;
}

const DOMAIN_ENTRIES: ISearchEntry[] = [
  {
    key: 'domain-summary',
    label: DashboardHeaderConstants.cloudSummary,
    description: 'Overview of foundations, accounts and projects',
    kind: 'domain',
    tags: ['summary', 'overview', 'environment'],
    dashboardTitle: DashboardHeaderConstants.cloudSummary,
    sidebarKey: SideBarKey.CLOUD_SUMMARY,
  },
  {
    key: 'domain-discover',
    label: DashboardHeaderConstants.discoverAccess,
    description: 'Discover and assess cloud foundations',
    kind: 'domain',
    tags: ['discover', 'assess', 'cloud strategy'],
    dashboardTitle: DashboardHeaderConstants.discoverAccess,
    sidebarKey: SideBarKey.DISCOVERASSESS,
  },
  {
    key: 'domain-build',
    label: DashboardHeaderConstants.buildDeploy,
    description: 'Build and deploy foundations',
    kind: 'domain',
    tags: ['build', 'deploy', 'environment build'],
    dashboardTitle: DashboardHeaderConstants.buildDeploy,
    sidebarKey: SideBarKey.BUILD,
  },
  {
    key: 'domain-migrate',
    label: DashboardHeaderConstants.migrateModernize,
    description: 'Migrate and modernize workloads',
    kind: 'domain',
    tags: ['migrate', 'modernize', 'runways'],
    dashboardTitle: DashboardHeaderConstants.migrateModernize,
    sidebarKey: SideBarKey.MIGRATE_MODERNIZE,
  },
  {
    key: 'domain-operate',
    label: DashboardHeaderConstants.operate,
    description: 'Site reliability engineering, monitoring & incidents',
    kind: 'domain',
    tags: ['operate', 'sre', 'reliability'],
    dashboardTitle: DashboardHeaderConstants.operate,
    sidebarKey: SideBarKey.OPERATE,
  },
  {
    key: 'domain-security',
    label: DashboardHeaderConstants.securityGovernance,
    description: 'Security & governance posture',
    kind: 'domain',
    tags: ['security', 'governance', 'compliance'],
    dashboardTitle: DashboardHeaderConstants.securityGovernance,
    sidebarKey: SideBarKey.SECURITY,
  },
  {
    key: 'domain-ai-engine',
    label: DashboardHeaderConstants.aiEngine,
    description: 'AI Engine console — agents, skills, models & guardrails',
    kind: 'domain',
    tags: ['ai', 'genai', 'agents', 'skills', 'models', 'guardrails'],
    dashboardTitle: DashboardHeaderConstants.aiEngine,
    sidebarKey: SideBarKey.AI_ENGINE,
  },
];

const buildCapabilityEntries = (): ISearchEntry[] =>
  Object.values(capabilitiesData).map((cap: ICapability) => ({
    key: `cap-${cap.id}`,
    label: cap.title,
    description: cap.description,
    kind: 'capability',
    tags: ['ai', ...cap.tags.map((t) => t.toLowerCase())],
    route: cap.route,
  }));

const buildAllEntries = (): ISearchEntry[] => [...DOMAIN_ENTRIES, ...buildCapabilityEntries()];

const isExternalRoute = (route: string): boolean => /^https?:\/\//i.test(route);

const CapabilitySearch: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [query, setQuery] = useState<string>('');

  const allEntries = useMemo(buildAllEntries, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allEntries;
    return allEntries.filter((entry) => {
      const haystack = [
        entry.label,
        entry.description || '',
        ...entry.tags,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allEntries, query]);

  const groupedOptions = useMemo(() => {
    const domains = filtered.filter((e) => e.kind === 'domain');
    const caps = filtered.filter((e) => e.kind === 'capability');

    const toOption = (entry: ISearchEntry) => ({
      value: entry.key,
      label: (
        <div className="capability-search-option" data-kind={entry.kind}>
          <div className="capability-search-option__title">
            <span className="capability-search-option__name">{entry.label}</span>
            <span className="capability-search-option__badge">
              {entry.kind === 'domain' ? 'Domain' : 'AI Capability'}
            </span>
          </div>
          {entry.description && (
            <div className="capability-search-option__desc">{entry.description}</div>
          )}
        </div>
      ),
      entry,
    });

    const groups = [];
    if (domains.length) {
      groups.push({
        label: <span className="capability-search-group">Domains</span>,
        options: domains.map(toOption),
      });
    }
    if (caps.length) {
      groups.push({
        label: <span className="capability-search-group">AI Capabilities</span>,
        options: caps.map(toOption),
      });
    }
    return groups;
  }, [filtered]);

  const handleSelect = (_value: string, option: any) => {
    const entry: ISearchEntry | undefined = option?.entry;
    if (!entry) return;

    if (entry.kind === 'domain') {
      if (entry.dashboardTitle) {
        dispatch(DashboardActions.setActiveDashboardTitleState(entry.dashboardTitle));
      }
      if (entry.sidebarKey) {
        dispatch(SidebarActions.setSidebarKey(entry.sidebarKey as string));
      }
      history.push(routes.dashboard);
    } else if (entry.route) {
      if (isExternalRoute(entry.route)) {
        window.open(entry.route, '_blank', 'noopener,noreferrer');
      } else {
        history.push(entry.route);
      }
    }
    setQuery('');
  };

  return (
    <div className="capability-search">
      <AutoComplete
        value={query}
        onChange={(val) => setQuery(val)}
        onSelect={handleSelect}
        options={groupedOptions as any}
        popupClassName="capability-search__dropdown"
        getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
        style={{ width: '100%' }}
        defaultActiveFirstOption={false}
        notFoundContent={<span className="capability-search__empty">No matches</span>}
      >
        <Input
          allowClear
          size="middle"
          prefix={<SearchOutlined className="capability-search__icon" />}
          placeholder="Search capabilities, domains, AI features…"
          className="capability-search__input"
        />
      </AutoComplete>
    </div>
  );
};

export default CapabilitySearch;
